import { useApolloClient } from "@apollo/client";
import { getDataOrNull, zipOrThrow } from "@msys/common";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { PimProductPreferenceScope } from "../../../clients/graphqlTypes.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../../features/products/Product.generated.js";
import { articleNumberIdentifierKey } from "../../features/products/productPreferences.js";
import {
  ProductPreferencesPage_PreferredProductDocument,
  ProductPreferencesPage_PreferredProductQuery,
  ProductPreferencesPage_PreferredProductQueryVariables,
} from "./ProductPreferences.generated.js";

type ArticleNumberIdentifier = { supplierId: string; articleNumber: string };

type PreferenceAttributions =
  ProductSearchItem__ProductSearchResultFragment["preferences"];

export type PreferredProduct = NonNullable<
  Extract<
    ProductPreferencesPage_PreferredProductQuery["pimGetProduct"],
    { __typename: "PimGetProductResult" }
  >["product"]
>;

type PageArticle = {
  scope: PimProductPreferenceScope;
  articleNumberIdentifier: ArticleNumberIdentifier;
};

/**
 * The products behind the visible page's article preferences, in the shape the product list
 * views render.
 *
 * A preference stores only `(supplierId, articleNumber)` and PIM offers no by-identifier
 * product search, so each article is fetched by identifier and adapted into a search result,
 * for the visible page only. Fetched products are kept across pages and refetches, so what is
 * on screen stays while a page loads. An article the catalogue no longer returns is kept as a
 * row carrying just its identifier: dropping it would leave a preference the user can neither
 * see nor remove.
 */
export function usePreferredProducts(
  pageArticles: readonly PageArticle[],
  /** The product type the articles are preferred for; `null` where there are none. */
  productType: { productTypeKey: string; productTypeSystemKey: string } | null
) {
  const client = useApolloClient();

  const identifiers = React.useMemo(
    () => [
      // One fetch per article: an article preferred at both scopes is one product.
      ...new Map(
        pageArticles.map(
          ({ articleNumberIdentifier: { supplierId, articleNumber } }) => [
            articleNumberIdentifierKey({ supplierId, articleNumber }),
            { supplierId, articleNumber },
          ]
        )
      ).values(),
    ],
    [pageArticles]
  );

  const [fetched, setFetched] = React.useState<
    ReadonlyMap<string, ProductSearchItem__ProductSearchResultFragment>
  >(new Map());
  // Kept apart from `fetched` so a failed lookup is retried the next time the page changes.
  const [unresolved, setUnresolved] = React.useState<
    ReadonlyMap<string, ProductSearchItem__ProductSearchResultFragment>
  >(new Map());

  const missing = React.useMemo(
    () =>
      identifiers.filter(
        identifier => !fetched.has(articleNumberIdentifierKey(identifier))
      ),
    [identifiers, fetched]
  );
  // Keyed by content: a new array with the same articles must not restart a fetch in flight.
  const missingKey = missing.map(articleNumberIdentifierKey).join("|");
  const missingRef = React.useRef(missing);
  missingRef.current = missing;

  React.useEffect(() => {
    const missing = missingRef.current;
    if (missing.length === 0) return;
    let cancelled = false;

    Promise.allSettled(
      missing.map(async identifier => {
        const result = await client.query<
          ProductPreferencesPage_PreferredProductQuery,
          ProductPreferencesPage_PreferredProductQueryVariables
        >({
          query: ProductPreferencesPage_PreferredProductDocument,
          variables: identifier,
        });
        return getDataOrNull(result.data.pimGetProduct)?.product ?? null;
      })
    ).then(results => {
      if (cancelled) return;
      // A failed lookup degrades to the identifier-only row, so the rest of the list stays
      // visible and the failed preference stays removable.
      const settled = zipOrThrow(missing, results).map(
        ([identifier, result]) => ({
          key: articleNumberIdentifierKey(identifier),
          identifier,
          product:
            result.status === "fulfilled" && result.value !== null
              ? toProductSearchResult(result.value)
              : null,
        })
      );
      const resolved = settled.flatMap(({ key, product }) =>
        product ? [[key, product] as const] : []
      );
      const resolvedKeys = new Set(resolved.map(([key]) => key));
      setFetched(current => new Map([...current, ...resolved]));
      setUnresolved(
        current =>
          new Map([
            ...[...current].filter(([key]) => !resolvedKeys.has(key)),
            ...settled.flatMap(({ key, identifier, product }) =>
              product
                ? []
                : [[key, toUnresolvedProductSearchResult(identifier)] as const]
            ),
          ])
      );
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (failure?.reason instanceof Error)
        enqueueSnackbar(failure.reason.message, { variant: "error" });
    });

    return () => {
      cancelled = true;
    };
  }, [client, missingKey]);

  // The attribution a search result carries, stamped from the list rather than fetched: the
  // product query has no `preferences` field, and the list stays in step with every write.
  const attributionsByArticle = React.useMemo(() => {
    const byArticle = new Map<string, PreferenceAttributions>();
    if (productType === null) return byArticle;
    for (const { scope, articleNumberIdentifier } of pageArticles) {
      const key = articleNumberIdentifierKey(articleNumberIdentifier);
      byArticle.set(key, [
        ...(byArticle.get(key) ?? []),
        {
          __typename: "PimProductPreferenceAttribution",
          scope,
          productType: {
            __typename: "PimProductPreferenceProductType",
            ...productType,
          },
        },
      ]);
    }
    return byArticle;
  }, [pageArticles, productType]);

  const productOf = React.useCallback(
    (
      identifier: ArticleNumberIdentifier
    ): ProductSearchItem__ProductSearchResultFragment | null => {
      const key = articleNumberIdentifierKey(identifier);
      const product = fetched.get(key) ?? unresolved.get(key);
      return product
        ? { ...product, preferences: attributionsByArticle.get(key) ?? [] }
        : null;
    },
    [fetched, unresolved, attributionsByArticle]
  );

  return {
    // Nothing of the page to show yet is the only state worth a spinner.
    loading:
      identifiers.length > 0 &&
      identifiers.every(identifier => {
        const key = articleNumberIdentifierKey(identifier);
        return !fetched.has(key) && !unresolved.has(key);
      }),
    productOf,
  };
}

export function toProductSearchResult(
  product: PreferredProduct
): ProductSearchItem__ProductSearchResultFragment {
  const shared = {
    // The attribution is not on the product — it is stamped from the preference list the
    // hook already holds, so it stays in step with a write instead of with the last fetch.
    preferences: [],
    attributions: [],
    id: product.id,
    articleNumber: product.articleNumber,
    gtin: product.gtin,
    supplierId: product.supplierId,
    supplier: product.supplier,
    texts: product.texts,
    attachments: product.attachments,
    pricing: product.pricing,
    status: product.status,
    organisationFavourite: product.organisationFavourite,
    manufacturerArticleNumber: product.manufacturerArticleNumber,
    // The search result flattens the branding to the brand's name.
    brand: product.branding?.brand.title ?? null,
    // The product query exposes the classification as PimProductTypeRef, whose label is
    // the search result's PimProductTypeMinimal title.
    productType: product.productType
      ? {
          __typename: "PimProductTypeMinimal" as const,
          id: product.productType.id,
          key: product.productType.key,
          title: product.productType.label,
          productTypeSystemKey: product.productType.productTypeSystemKey,
        }
      : null,
  };

  return product.__typename === "PimSupplierProduct"
    ? {
        __typename: "PimSupplierProductSearchResult",
        ...shared,
        supplierFavourite: product.supplierFavourite,
      }
    : { __typename: "PimCustomSupplierProductSearchResult", ...shared };
}

export function toUnresolvedProductSearchResult(
  identifier: ArticleNumberIdentifier
): ProductSearchItem__ProductSearchResultFragment {
  return {
    __typename: "PimCustomSupplierProductSearchResult",
    preferences: [],
    attributions: [],
    id: articleNumberIdentifierKey(identifier),
    articleNumber: identifier.articleNumber,
    gtin: null,
    supplierId: identifier.supplierId,
    supplier: {
      __typename: "PimSupplier",
      id: identifier.supplierId,
      title: "",
      logo: null,
    },
    texts: {
      __typename: "PimProductTexts",
      title: identifier.articleNumber,
      description: null,
      extendedDescription: null,
    },
    attachments: [],
    pricing: null,
    status: null,
    organisationFavourite: false,
    manufacturerArticleNumber: null,
    brand: null,
    productType: null,
  };
}
